import { useEffect } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { ptBR } from './pt-BR';
import { enGB } from './en-GB';
import { esES } from './es-ES';
import { frFR } from './fr-FR';
import { itIT } from './it-IT';
import { ar } from './ar';
import { zhCN } from './zh-CN';

export type Locale = 'pt-BR' | 'en-GB' | 'es-ES' | 'fr-FR' | 'it-IT' | 'ar' | 'zh-CN';
export type Translations = typeof ptBR;

const translations: Record<Locale, Translations> = {
  'pt-BR': ptBR,
  'en-GB': enGB,
  'es-ES': esES,
  'fr-FR': frFR,
  'it-IT': itIT,
  'ar': ar,
  'zh-CN': zhCN,
};

const SUPPORTED_LOCALES: Locale[] = ['pt-BR', 'en-GB', 'es-ES', 'fr-FR', 'it-IT', 'ar', 'zh-CN'];

/**
 * Detecta locale do navegador
 */
function detectLocale(): Locale {
  if (typeof window === 'undefined') return 'en-GB';

  const browserLang = navigator.language;

  if (browserLang.startsWith('pt')) return 'pt-BR';
  if (browserLang.startsWith('es')) return 'es-ES';
  if (browserLang.startsWith('fr')) return 'fr-FR';
  if (browserLang.startsWith('it')) return 'it-IT';
  if (browserLang.startsWith('ar')) return 'ar';
  if (browserLang.startsWith('zh')) return 'zh-CN';

  return 'en-GB';
}

interface LanguageState {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  toggleLocale: () => void;
}

export const useLanguageStore = create<LanguageState>()(
  persist(
    (set, get) => ({
      locale: detectLocale(),
      setLocale: (locale: Locale) => {
        set({ locale });
        document.documentElement.lang = locale;
      },
      toggleLocale: () => {
        const currentIndex = SUPPORTED_LOCALES.indexOf(get().locale);
        const nextIndex = (currentIndex + 1) % SUPPORTED_LOCALES.length;
        get().setLocale(SUPPORTED_LOCALES[nextIndex]);
      },
    }),
    {
      name: 'keepnota-locale',
    }
  )
);

/**
 * Hook para usar traducoes
 */
export function useTranslation() {
  const { locale, setLocale, toggleLocale } = useLanguageStore();

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const t = translations[locale];

  return {
    t,
    locale,
    setLocale,
    toggleLocale,
    supportedLocales: SUPPORTED_LOCALES,
    isPortuguese: locale === 'pt-BR',
    isEnglish: locale === 'en-GB',
    isSpanish: locale === 'es-ES',
    isFrench: locale === 'fr-FR',
    isItalian: locale === 'it-IT',
    isArabic: locale === 'ar',
    isChinese: locale === 'zh-CN',
  };
}

export { ptBR, enGB, esES, frFR, itIT, ar, zhCN, SUPPORTED_LOCALES };
